import { Immutable } from "immer";
import React, { useId, useState } from "react";

import type { AccordionSection as Value } from "@reactivated";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/components/accordion-block.html`` (toppers ``AccordionSection`` block).
 *
 * Each item owns its open/close state: clicking the trigger toggles
 * ``accordion--is-active`` on the ``<button>``, ``accordion-target--is-active``
 * on its target, and ``aria-expanded``. Items start collapsed.
 *
 * The ``id`` / ``aria-controls`` pairing uses ``useId()`` for a stable,
 * SSR-safe, collision-free value (the id is a11y-only, never fed to a
 * CSS/JS selector), matching the shared ``tsi-common-react`` AccordionBlock.
 *
 * ``block_header`` / ``header`` are plain ``CharBlock`` values -> rendered as
 * escaped text (legacy emitted ``{{ value.block_header }}`` / ``{{ section.header }}``
 * without ``|safe``). ``body`` is a ``RichTextBlock`` whose serialized value is
 * an already-rendered HTML string (wrapped in ``<div class="rich-text">`` by the
 * server). Legacy placed ``{{ section.body }}`` directly inside the target div,
 * so we raw-inject via ``dangerouslySetInnerHTML`` to match exactly (using
 * thelabui's ``<RichTextBlock>`` would add an extra wrapper div).
 */
type IProps = Immutable<{
    value: Value;
}>;

type Section = NonNullable<Value["sections"]>[number];

const AccordionSectionItem = (
    props: Immutable<{ section: Section; targetID: string }>,
): React.JSX.Element => {
    const { section, targetID } = props;
    const [isOpen, setIsOpen] = useState(false);
    return (
        <li className="accordion__list-item">
            <button
                className={
                    isOpen ? "accordion accordion--is-active" : "accordion"
                }
                aria-expanded={isOpen ? "true" : "false"}
                aria-controls={targetID}
                onClick={(event) => {
                    event.preventDefault();
                    setIsOpen((open) => !open);
                }}
            >
                {section.header}
            </button>
            <div
                id={targetID}
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
                dangerouslySetInnerHTML={{
                    __html: section.body ?? "",
                }}
            />
        </li>
    );
};

export const AccordionSection = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const sections = v.sections ?? [];
    // `sections` is a Wagtail ListBlock (no per-item id in the generated type),
    // so the index is the only stable key. The same index drives the a11y id
    // suffix, mirroring the legacy 1-indexed `forloop.counter`.
    const idBase = useId();

    return (
        <div className="accordion__container">
            <h2 className="accordion__title">{v.block_header}</h2>

            <ul className="accordion__list">
                {sections.map((section, index) => (
                    <AccordionSectionItem
                        key={index}
                        section={section}
                        targetID={`${idBase}menu-content-${index + 1}`}
                    />
                ))}
            </ul>
        </div>
    );
};
